import React from 'react';
import { ShieldCheck, Heart, Briefcase, Plane, Clock, Users } from 'lucide-react';
import { StorytellingLayout, ExpandableCard } from '../base';

/**
 * Tela de storytelling para seguro viagem
 * 
 * Single Source of Truth: Backend envia todos os dados calculados.
 * Frontend apenas renderiza.
 * 
 * Schema esperado (details):
 * - company, plan_name
 * - coverage_amount, coverage_currency
 * - start_date, end_date, days
 * - passengers, destination
 * - coverages: { medical, dental, baggage, ... }
 */
export default function StorytellingInsurance({
  block,
  stepIndicator,
  narrativeText,
  ctaText,
  onContinue,
  onBack,
  onShare
}) {
  const details = block.details || {};
  const coverages = details.coverages || {};

  // Imagem de fundo padrão
  const backgroundImage = 'https://images.unsplash.com/photo-1436491865332-7a61a109db05?w=800&q=80';

  // Formata data
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  // Formata cobertura principal
  const formatCoverage = () => {
    const amount = details.coverage_amount || 0;
    const currency = details.coverage_currency || 'USD';
    return `${currency} ${amount.toLocaleString('pt-BR')}`;
  };

  // Filtra coberturas com valor
  const activeCoverages = Object.entries(coverages).filter(([_, value]) => {
    if (!value) return false;
    const str = String(value).toLowerCase();
    return str !== 'não' && str !== 'nao' && str !== 'no' && str !== '0' && str !== '';
  });

  // Ícone de cobertura
  const getCoverageIcon = (key) => {
    const iconProps = { size: 14, color: '#5DD4C3', strokeWidth: 2 };
    switch (key) {
      case 'medical':
      case 'dental':
      case 'pharmaceutical':
        return <Heart {...iconProps} />;
      case 'baggage':
      case 'baggage_delay':
        return <Briefcase {...iconProps} />;
      case 'flight_delay':
      case 'trip_cancellation':
        return <Plane {...iconProps} />;
      default:
        return <ShieldCheck {...iconProps} />;
    }
  };

  // Label das coberturas
  const coverageLabels = {
    medical: 'Despesas médicas',
    dental: 'Despesas odontológicas',
    baggage: 'Extravio de bagagem',
    baggage_delay: 'Atraso de bagagem',
    trip_cancellation: 'Cancelamento de viagem',
    flight_delay: 'Atraso de voo',
    personal_accident: 'Morte acidental',
    civil_liability: 'Responsabilidade civil',
    pharmaceutical: 'Despesas farmacêuticas',
    telemedicine: 'Telemedicina 24h',
    covid: 'Cobertura COVID-19'
  };

  const cardContent = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      {/* Seguradora e plano */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
        <div style={{
          width: '48px',
          height: '48px',
          backgroundColor: 'rgba(93, 212, 195, 0.15)',
          borderRadius: '0.75rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0
        }}>
          <ShieldCheck size={24} color="#5DD4C3" strokeWidth={2} />
        </div>
        <div>
          <p style={{
            fontSize: '1.0625rem',
            fontWeight: 600,
            color: 'white',
            margin: 0,
            lineHeight: 1.3
          }}>
            {details.company || 'Seguro Viagem'}
          </p>
          {details.plan_name && (
            <p style={{
              fontSize: '0.8125rem',
              color: 'rgba(255, 255, 255, 0.6)',
              margin: '0.25rem 0 0 0'
            }}>
              Plano {details.plan_name}
            </p>
          )}
        </div>
      </div>

      {/* Info principal: cobertura, dias, passageiros */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: '0.75rem',
        paddingTop: '0.75rem',
        borderTop: '1px solid rgba(255, 255, 255, 0.1)'
      }}>
        {/* Cobertura */}
        <div style={{ textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.375rem' }}>
          <div style={{
            width: '44px',
            height: '44px',
            backgroundColor: 'rgba(93, 212, 195, 0.15)',
            borderRadius: '0.5rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <ShieldCheck size={20} color="#5DD4C3" strokeWidth={2} />
          </div>
          <p style={{ fontSize: '0.875rem', fontWeight: 600, color: 'white', margin: 0 }}>
            {formatCoverage()}
          </p>
          <p style={{ fontSize: '0.6875rem', color: 'rgba(255, 255, 255, 0.6)', margin: 0 }}>
            Cobertura
          </p>
        </div>

        {/* Dias */}
        <div style={{ textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.375rem' }}>
          <div style={{
            width: '44px',
            height: '44px',
            backgroundColor: 'rgba(93, 212, 195, 0.15)',
            borderRadius: '0.5rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <Clock size={20} color="#5DD4C3" strokeWidth={2} />
          </div>
          <p style={{ fontSize: '0.875rem', fontWeight: 600, color: 'white', margin: 0 }}>
            {details.days || '-'}
          </p>
          <p style={{ fontSize: '0.6875rem', color: 'rgba(255, 255, 255, 0.6)', margin: 0 }}>
            Dias
          </p>
        </div>

        {/* Passageiros */}
        <div style={{ textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.375rem' }}>
          <div style={{
            width: '44px',
            height: '44px',
            backgroundColor: 'rgba(93, 212, 195, 0.15)',
            borderRadius: '0.5rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <Users size={20} color="#5DD4C3" strokeWidth={2} />
          </div>
          <p style={{ fontSize: '0.875rem', fontWeight: 600, color: 'white', margin: 0 }}>
            {details.passengers || 1}
          </p>
          <p style={{ fontSize: '0.6875rem', color: 'rgba(255, 255, 255, 0.6)', margin: 0 }}>
            Passageiros
          </p>
        </div>
      </div>

      {/* Período */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingTop: '0.75rem',
        borderTop: '1px solid rgba(255, 255, 255, 0.1)'
      }}>
        <div>
          <p style={{ fontSize: '0.8125rem', color: 'rgba(255, 255, 255, 0.6)', margin: 0 }}>
            Início
          </p>
          <p style={{ fontSize: '0.9375rem', fontWeight: 600, color: 'white', margin: '0.25rem 0 0 0' }}>
            {formatDate(details.start_date || block.date)}
          </p>
        </div>
        <div style={{ textAlign: 'right' }}>
          <p style={{ fontSize: '0.8125rem', color: 'rgba(255, 255, 255, 0.6)', margin: 0 }}>
            Fim
          </p>
          <p style={{ fontSize: '0.9375rem', fontWeight: 600, color: 'white', margin: '0.25rem 0 0 0' }}>
            {formatDate(details.end_date)}
          </p>
        </div>
      </div>

      {/* Top 3 coberturas preview */}
      {activeCoverages.length > 0 && (
        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '0.5rem',
          paddingTop: '0.75rem',
          borderTop: '1px solid rgba(255, 255, 255, 0.1)'
        }}>
          {activeCoverages.slice(0, 3).map(([key, value]) => (
            <div key={key} style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.5rem 0.75rem',
              backgroundColor: 'rgba(93, 212, 195, 0.15)',
              borderRadius: '0.5rem'
            }}>
              {getCoverageIcon(key)}
              <span style={{ fontSize: '0.8125rem', fontWeight: 600, color: '#5DD4C3' }}>
                {coverageLabels[key] || key}
              </span>
            </div>
          ))}
          {activeCoverages.length > 3 && (
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              padding: '0.5rem 0.75rem',
              backgroundColor: 'rgba(255, 255, 255, 0.08)',
              borderRadius: '0.5rem'
            }}>
              <span style={{ fontSize: '0.8125rem', color: 'rgba(255, 255, 255, 0.6)' }}>
                +{activeCoverages.length - 3} coberturas
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );

  const expandedContent = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
      {/* Todas as coberturas */}
      {activeCoverages.length > 0 && (
        <div>
          <p style={{ fontSize: '0.875rem', fontWeight: 600, color: 'white', margin: '0 0 0.5rem 0' }}>
            Coberturas incluídas
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {activeCoverages.map(([key, value]) => (
              <div key={key} style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '0.5rem 0',
                borderBottom: '1px solid rgba(255, 255, 255, 0.06)'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  {getCoverageIcon(key)}
                  <span style={{ fontSize: '0.8125rem', color: 'rgba(255, 255, 255, 0.8)' }}>
                    {coverageLabels[key] || key}
                  </span>
                </div>
                <span style={{ fontSize: '0.8125rem', fontWeight: 600, color: '#5DD4C3' }}>
                  {String(value).toLowerCase() === 'sim' ? '✓' : value}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Destino */}
      {details.destination && (
        <div>
          <p style={{ fontSize: '0.75rem', color: 'rgba(255, 255, 255, 0.6)', margin: 0 }}>Região de cobertura</p>
          <p style={{ fontSize: '0.875rem', fontWeight: 600, color: 'white', margin: 0 }}>
            {details.destination}
          </p>
        </div>
      )}
    </div>
  );

  return (
    <StorytellingLayout
      backgroundImage={backgroundImage}
      title="Seguro Viagem"
      narrativeText={narrativeText}
      stepIndicator={stepIndicator}
      ctaText={ctaText}
      onContinue={onContinue}
      onBack={onBack}
      onShare={onShare}
    >
      <ExpandableCard expandedContent={expandedContent}>
        {cardContent}
      </ExpandableCard>
    </StorytellingLayout>
  );
}
